import React, { useState, useEffect, useRef } from "react";
import styles from "./CheckpointDetailModal.module.css";
import { open_api_with_cookies } from "../../Utils/network";
import { safeParseHTML } from "./challengeUtils";
import HlsVideoPlayer from "../../Components/VideoPlayer/HlsVideoPlayer";

// Video player component to handle different video types
const VideoPlayer = ({ videoUrl, posterImage }) => {
  // Utility functions for video URL detection
  const isYouTubeUrl = (url) => {
    return (
      url.includes("youtube.com") ||
      url.includes("youtu.be") ||
      url.includes("m.youtube.com")
    );
  };

  const isHLSUrl = (url) => {
    return url.includes(".m3u8");
  };

  const extractYouTubeId = (url) => {
    const patterns = [
      /youtube\.com\/watch\?v=([^&]+)/,
      /youtu\.be\/([^?]+)/,
      /youtube\.com\/embed\/([^?]+)/,
      /m\.youtube\.com\/watch\?v=([^&]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  if (isYouTubeUrl(videoUrl)) {
    const videoId = extractYouTubeId(videoUrl);
    if (!videoId) {
      return <div className={styles.videoError}>Invalid YouTube URL</div>;
    }

    return (
      <div className={styles.youtubeContainer}>
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
          title="Checkpoint Video"
          className={styles.youtubeIframe}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    );
  }

  if (isHLSUrl(videoUrl)) {
    return (
      <div
        className={styles.hlsContainer}
        style={{
          backgroundImage: posterImage ? `url(${posterImage})` : "none",
        }}
      >
        <HlsVideoPlayer
          currentVideo={{
            url: videoUrl,
          }}
        />
      </div>
    );
  }

  // Regular MP4 or other video formats
  return (
    <video
      controls
      className={styles.video}
      poster={posterImage}
      preload="metadata"
      playsInline
    >
      <source src={videoUrl} type="video/mp4" />
      <source src={videoUrl} type="video/webm" />
      <source src={videoUrl} type="video/ogg" />
      Your browser does not support the video tag.
    </video>
  );
};

const CheckpointDetailModal = ({
  isOpen,
  onClose,
  checkpointSlug,
  challengeSlug,
  challengeBatch,
  challengePurchaseId,
}) => {
  const [checkpointData, setCheckpointData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localChallengeBatch, setLocalChallengeBatch] =
    useState(challengeBatch);

  // Submission form states
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  const [submissionData, setSubmissionData] = useState({
    text: "",
    attachment: null,
    isAnonymous: false,
    shareAttachment: false,
  });
  const [fileError, setFileError] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const modalRef = useRef(null);
  const contentRef = useRef(null);
  const fileInputRef = useRef(null);
  const submissionFormRef = useRef(null);

  // Add function to fetch challenge batch
  const fetchChallengeBatch = async () => {
    if (!challengePurchaseId) {
      setError("Challenge purchase ID not available");
      return null;
    }

    try {
      const response = await open_api_with_cookies.get(
        `challenge/purchase/${challengePurchaseId}/batch/`
      );
      if (response.status === 200) {
        setLocalChallengeBatch(response.data.batch_slug);
        return response.data.batch_slug;
      }
    } catch (err) {
      console.error("Error fetching challenge batch:", err);
      setError("Failed to fetch challenge batch");
    }
    return null;
  };

  // Fetch checkpoint details
  const fetchCheckpointDetails = async () => {
    if (!checkpointSlug) return;

    try {
      setLoading(true);
      setError(null);

      // Ensure we have challenge batch before proceeding
      let batchToUse = localChallengeBatch;
      if (!batchToUse && challengePurchaseId) {
        batchToUse = await fetchChallengeBatch();
        if (!batchToUse) {
          setError("Unable to determine challenge batch");
          return;
        }
      }

      if (!batchToUse) {
        setError("Challenge batch not available");
        return;
      }

      const response = await open_api_with_cookies.get(
        `challenge/checkpoint/${checkpointSlug}/batch/${batchToUse}/`
      );

      if (response.status === 200) {
        setCheckpointData(response.data);
      }
    } catch (err) {
      console.error("Error fetching checkpoint details:", err);
      setError("Failed to load checkpoint details");
    } finally {
      setLoading(false);
    }
  };

  // File handling functions
  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "application/pdf",
    ];

    if (file.size > maxSize) {
      return "File size must be less than 10MB";
    }

    if (!allowedTypes.includes(file.type)) {
      return "Please upload an image (JPG, PNG, GIF) or PDF file";
    }

    return null;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const error = validateFile(file);
      if (error) {
        setFileError(error);
        return;
      }

      setFileError("");
      setSubmissionData((prev) => ({ ...prev, attachment: file }));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      const error = validateFile(file);
      if (error) {
        setFileError(error);
        return;
      }

      setFileError("");
      setSubmissionData((prev) => ({ ...prev, attachment: file }));
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const removeFile = () => {
    setSubmissionData((prev) => ({ ...prev, attachment: null }));
    setFileError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle checkpoint completion
  const handleCompleteCheckpoint = () => {
    setShowSubmissionForm(true);

    // Scroll to the submission form after it renders
    setTimeout(() => {
      if (submissionFormRef.current) {
        submissionFormRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 300); // Increased delay to ensure the form is fully rendered
  };

  // Handle submission form submission
  const handleSubmitCheckpoint = async () => {
    if (!checkpointData) {
      setError("Checkpoint data not available");
      return;
    }

    // Ensure we have challenge batch before submitting
    let batchToUse = localChallengeBatch;
    if (!batchToUse && challengePurchaseId) {
      batchToUse = await fetchChallengeBatch();
      if (!batchToUse) {
        setError("Unable to determine challenge batch for submission");
        return;
      }
    }

    if (!batchToUse) {
      setError("Challenge batch not available for submission");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null); // Clear any previous errors

      const formData = new FormData();

      // Add text (with default if empty)
      const defaultText = submissionData.isAnonymous
        ? "Someone has completed the checkpoint"
        : `${checkpointData.user_name || "User"} has completed ${checkpointData.checkpoint_title || "the checkpoint"}`;

      const finalText = submissionData.text?.trim()
        ? submissionData.text.trim()
        : defaultText;

      formData.append("text", finalText);
      formData.append(
        "is_anonymous",
        submissionData.isAnonymous ? "true" : "false"
      );
      formData.append(
        "share_attachment",
        submissionData.shareAttachment ? "true" : "false"
      );

      // Add file if selected
      if (submissionData.attachment) {
        formData.append(
          "attachment",
          submissionData.attachment,
          submissionData.attachment.name
        );
      }

      const endpointId = checkpointData.id || checkpointSlug;
      const apiEndpoint = `challenge/checkpoint/${endpointId}/batch/${batchToUse}/submit/`;

      const response = await open_api_with_cookies.post(apiEndpoint, formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          // Could add progress UI here if needed
        },
      });

      if (response.status === 200 || response.status === 201) {
        // Refresh checkpoint data to show updated status
        await fetchCheckpointDetails();
        setShowSubmissionForm(false);
        // Reset form
        setSubmissionData({
          text: "",
          attachment: null,
          isAnonymous: false,
          shareAttachment: false,
        });
      } else {
        setError(`Submission failed with status: ${response.status}`);
      }
    } catch (err) {
      if (err.response?.data?.message) {
        setError(`Submission failed: ${err.response.data.message}`);
      } else if (err.response?.data?.error) {
        setError(`Submission failed: ${err.response.data.error}`);
      } else if (err.response?.status) {
        setError(
          `Submission failed: ${err.response.status} ${err.response.statusText}`
        );
      } else {
        setError(
          "Failed to submit checkpoint. Please check your connection and try again."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle escape key and backdrop click
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };

    const handleBackdropClick = (e) => {
      if (modalRef.current && !contentRef.current?.contains(e.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleBackdropClick);

      // Lock body scroll, especially important for mobile full-screen experience
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.top = `-${window.scrollY}px`;
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleBackdropClick);

      // Restore body scroll
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";

      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    };
  }, [isOpen, onClose]);

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen && checkpointSlug) {
      fetchCheckpointDetails();
    }
  }, [isOpen, checkpointSlug]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCheckpointData(null);
      setError(null);
      setShowSubmissionForm(false);
      setSubmissionData({
        text: "",
        attachment: null,
        isAnonymous: false,
        shareAttachment: false,
      });
      setFileError("");
    }
  }, [isOpen]);

  // Update local batch when prop changes
  useEffect(() => {
    setLocalChallengeBatch(challengeBatch);
  }, [challengeBatch]);

  if (!isOpen) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "#22c55e";
      case "unlocked":
        return "#3b82f6";
      case "locked":
        return "#64748b";
      default:
        return "#64748b";
    }
  };

  const getStatusText = (status, submissionStatus) => {
    if (submissionStatus?.has_submitted) {
      return submissionStatus.is_approved ? "Approved" : "Submitted";
    }

    switch (status) {
      case "completed":
        return "Completed";
      case "unlocked":
        return "Ready to Start";
      case "locked":
        return "Locked";
      default:
        return "Not Available";
    }
  };

  return (
    <div className={styles.modalOverlay} ref={modalRef}>
      <div className={styles.modalContent} ref={contentRef}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p>Loading checkpoint details...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className={styles.errorContainer}>
            <h3>Something went wrong</h3>
            <p>{error}</p>
            <button
              className={styles.retryButton}
              onClick={fetchCheckpointDetails}
            >
              Try Again
            </button>
          </div>
        )}

        {/* Content */}
        {checkpointData && (
          <div className={styles.contentContainer}>
            {/* Hero Section */}
            <div className={styles.heroSection}>
              {checkpointData.image && (
                <div className={styles.mediaContainer}>
                  <img
                    src={checkpointData.image}
                    alt={checkpointData.checkpoint_title}
                    className={styles.heroImage}
                  />
                </div>
              )}

              <div className={styles.heroContent}>
                <div
                  className={styles.statusBadge}
                  style={{ background: getStatusColor(checkpointData.status) }}
                >
                  {getStatusText(
                    checkpointData.status,
                    checkpointData.submission_status
                  )}
                </div>

                <div className={styles.checkpointMeta}>
                  <span className={styles.dayNumber}>
                    Day {checkpointData.checkpoint_index}
                  </span>
                  <span className={styles.date}>
                    {new Date(checkpointData.date).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                </div>

                {checkpointData.title && (
                  <div
                    className={styles.heroTitle}
                    dangerouslySetInnerHTML={safeParseHTML(
                      checkpointData.title
                    )}
                  />
                )}

                {checkpointData.sub_title && (
                  <div
                    className={styles.heroSubtitle}
                    dangerouslySetInnerHTML={safeParseHTML(
                      checkpointData.sub_title
                    )}
                  />
                )}
              </div>
            </div>

            {/* Description Section */}
            {checkpointData.description && (
              <div className={styles.descriptionSection}>
                <div
                  className={styles.description}
                  dangerouslySetInnerHTML={safeParseHTML(
                    checkpointData.description
                  )}
                />
              </div>
            )}

            {/* Video Section */}
            {checkpointData.video && (
              <div className={styles.videoSection}>
                <VideoPlayer
                  videoUrl={checkpointData.video}
                  posterImage={checkpointData.image}
                />
              </div>
            )}

            {/* Link Section */}
            {checkpointData.link && (
              <div className={styles.linkSection}>
                <a
                  href={checkpointData.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.externalLink}
                >
                  📎 Additional Resource
                </a>
              </div>
            )}

            {/* Submission Status */}
            {checkpointData.submission_status && (
              <div className={styles.submissionSection}>
                {checkpointData.submission_status.has_submitted ? (
                  <div className={styles.submissionStatus}>
                    <div className={styles.submissionInfo}>
                      <span className={styles.submissionIcon}>
                        {checkpointData.submission_status.is_approved
                          ? "✅"
                          : "📋"}
                      </span>
                      <div>
                        <p className={styles.submissionText}>
                          {checkpointData.submission_status.is_approved
                            ? "Your submission has been approved!"
                            : "Your submission is under review"}
                        </p>
                        <p className={styles.submissionCount}>
                          Submission #
                          {checkpointData.submission_status.submission_count}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.noSubmission}>
                    <p>Complete this checkpoint to mark it as done</p>
                  </div>
                )}
              </div>
            )}

            {/* User Submission Data */}
            {checkpointData.user_submission &&
              checkpointData.submission_status?.has_submitted && (
                <div className={styles.userSubmissionSection}>
                  <div className={styles.userSubmissionHeader}>
                    <h4>Your Submission</h4>
                    <p className={styles.submissionDate}>
                      {new Date(
                        checkpointData.user_submission.created
                      ).toLocaleString()}
                    </p>
                  </div>

                  {checkpointData.user_submission.text && (
                    <div className={styles.userSubmissionText}>
                      <p>{checkpointData.user_submission.text}</p>
                    </div>
                  )}

                  {checkpointData.user_submission.attachment && (
                    <div className={styles.userSubmissionAttachment}>
                      <a
                        href={checkpointData.user_submission.attachment}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.attachmentLink}
                      >
                        📎 View Attachment
                      </a>
                    </div>
                  )}

                  <div className={styles.userSubmissionMeta}>
                    {checkpointData.user_submission.is_anonymous && (
                      <span className={styles.submissionMetaTag}>
                        Anonymous
                      </span>
                    )}
                  </div>
                </div>
              )}

            {/* Submission Form */}
            {showSubmissionForm && (
              <div
                className={styles.submissionFormSection}
                ref={submissionFormRef}
              >
                <div className={styles.submissionFormHeader}>
                  <h3>Complete Checkpoint</h3>
                  <p>Share your progress with the community (optional)</p>
                </div>

                <div className={styles.submissionForm}>
                  {/* Text Input */}
                  <div className={styles.textInputSection}>
                    <label
                      htmlFor="submissionText"
                      className={styles.inputLabel}
                    >
                      Share your thoughts (optional)
                    </label>
                    <textarea
                      id="submissionText"
                      className={styles.textInput}
                      placeholder="How did this checkpoint go? Share your experience..."
                      value={submissionData.text}
                      onChange={(e) =>
                        setSubmissionData((prev) => ({
                          ...prev,
                          text: e.target.value,
                        }))
                      }
                      rows={4}
                      maxLength={500}
                    />
                    <div className={styles.characterCount}>
                      {submissionData.text.length}/500
                    </div>
                  </div>

                  {/* File Upload */}
                  <div className={styles.fileUploadSection}>
                    <label className={styles.inputLabel}>
                      Attach a photo or document (optional)
                    </label>

                    <div
                      className={`${styles.fileDropZone} ${dragActive ? styles.dragActive : ""} ${submissionData.attachment ? styles.hasFile : ""}`}
                      onDrop={handleDrop}
                      onDragOver={handleDrag}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleFileChange}
                        className={styles.hiddenFileInput}
                      />

                      {submissionData.attachment ? (
                        <div className={styles.filePreview}>
                          <div className={styles.fileInfo}>
                            <span className={styles.fileIcon}>
                              {submissionData.attachment.type.startsWith(
                                "image/"
                              )
                                ? "🖼️"
                                : "📄"}
                            </span>
                            <div className={styles.fileDetails}>
                              <p className={styles.fileName}>
                                {submissionData.attachment.name}
                              </p>
                              <p className={styles.fileSize}>
                                {(
                                  submissionData.attachment.size /
                                  1024 /
                                  1024
                                ).toFixed(2)}{" "}
                                MB
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            className={styles.removeFileButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile();
                            }}
                            aria-label="Remove file"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <div className={styles.fileUploadPrompt}>
                          <div className={styles.uploadIcon}>📁</div>
                          <p className={styles.uploadText}>
                            Click to browse or drag & drop your file here
                          </p>
                          <p className={styles.uploadSubtext}>
                            Supports: JPG, PNG, GIF, PDF (max 10MB)
                          </p>
                        </div>
                      )}
                    </div>

                    {fileError && (
                      <div className={styles.fileError}>{fileError}</div>
                    )}
                  </div>

                  {/* Anonymous Toggle */}
                  <div className={styles.anonymousSection}>
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={submissionData.isAnonymous}
                        onChange={(e) =>
                          setSubmissionData((prev) => ({
                            ...prev,
                            isAnonymous: e.target.checked,
                          }))
                        }
                        className={styles.checkbox}
                      />
                      <span className={styles.checkboxText}>
                        Submit anonymously
                      </span>
                    </label>
                    <p className={styles.anonymousHelp}>
                      Your submission will be shared without your name
                    </p>
                  </div>

                  {/* Share Attachment Toggle */}
                  <div className={styles.anonymousSection}>
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={submissionData.shareAttachment}
                        onChange={(e) =>
                          setSubmissionData((prev) => ({
                            ...prev,
                            shareAttachment: e.target.checked,
                          }))
                        }
                        className={styles.checkbox}
                      />
                      <span className={styles.checkboxText}>
                        Share attachment publicly
                      </span>
                    </label>
                    <p className={styles.anonymousHelp}>
                      Your attachment will be visible to other participants
                    </p>
                  </div>

                  {/* Form Actions */}
                  <div className={styles.formActions}>
                    <button
                      type="button"
                      className={styles.cancelButton}
                      onClick={() => setShowSubmissionForm(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={styles.submitButton}
                      onClick={handleSubmitCheckpoint}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className={styles.buttonSpinner}></span>
                          Submitting...
                        </>
                      ) : (
                        <>✓ Submit Completion</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className={styles.actionSection}>
              {checkpointData.status === "unlocked" &&
                !checkpointData.submission_status?.has_submitted &&
                !showSubmissionForm && (
                  <button
                    className={styles.completeButton}
                    onClick={handleCompleteCheckpoint}
                  >
                    Continue
                  </button>
                )}

              <button className={styles.closeModalButton} onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckpointDetailModal;
